import { useCallback, useEffect, useRef, useState } from "react";
import { Linking, Platform } from "react-native";
import { YStack } from "tamagui";

import { useConstructPaymentDestination } from "@/api/generated";
import {
  PaymentDestinationStatus,
  type UserProgressionStateModel,
} from "@/api/generated/models";
import { Header, Heading, LinkText, Paragraph } from "@/components";
import { AD_CONVERSION_EVENTS } from "@/config/adConversionEvents";
import { useAnalytics } from "@/hooks/analytics";
import { captureMessage } from "@/sentry";
import { isClinicRequestExpiredError } from "@/utils/clinicRequestError";
import { getResetProgressionFromError } from "@/utils/progressionReset";
import { useUserProgression } from "../UserProgression/UserProgressionContext";

// How long the "you are being redirected" screen stays up before we send the user to Stripe.
// Long enough to read, short enough to feel like a redirect. Only gates the OPEN redirect — an
// already-paid progression skips ahead immediately.
const AUTO_REDIRECT_DELAY_MS = 1500;

type StripeRedirectScreenProps = {
  onContinue?: () => void;
  onPaymentBlocked: (progression: UserProgressionStateModel) => void;
  onClinicRequestExpired?: () => void;
};

type Destination = { kind: "paid" } | { kind: "open"; checkoutUrl: string };

/**
 * `stripe_redirect` step screen. Resolves the payment destination for the active progression as
 * soon as the screen appears: an already-paid progression advances the flow immediately, while an
 * open one shows a brief redirect screen and then sends the user to the hosted Stripe checkout. A
 * visible fallback link covers the case where the auto-redirect is blocked or slow. The
 * conversation id is carried through so Stripe can return the user to the originating conversation.
 */
export default function StripeRedirectScreen({
  onContinue,
  onPaymentBlocked,
  onClinicRequestExpired,
}: StripeRedirectScreenProps) {
  const progressionContext = useUserProgression();
  const progressionId = progressionContext?.progression.id;
  const conversationId =
    progressionContext?.progression.conversation_id ?? undefined;
  const productOption =
    progressionContext?.progression.metadata?.payment_product_option;
  const { track } = useAnalytics();
  const { mutateAsync: constructPaymentDestination } =
    useConstructPaymentDestination();
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  // Whichever path wins — advance vs. redirect — must happen exactly once.
  const actedRef = useRef(false);
  // The mount resolve and a fallback-link click can both resolve; only track checkout start once.
  const checkoutStartedRef = useRef(false);

  const advance = useCallback(() => {
    if (actedRef.current) return;
    actedRef.current = true;
    onContinue?.();
  }, [onContinue]);

  const redirect = useCallback((url: string) => {
    if (actedRef.current) return;
    actedRef.current = true;
    if (Platform.OS === "web") {
      // Navigate the current tab to Stripe rather than spawning a new one; Stripe's
      // success/cancel URLs bring the user back to the progression when they're done.
      window.location.assign(url);
    } else {
      Linking.openURL(url);
    }
  }, []);

  const resolveDestination =
    useCallback(async (): Promise<Destination | null> => {
      if (!progressionId) return null;
      const destination = await constructPaymentDestination({
        progressionId,
        data: { conversation_id: conversationId },
      });
      if (destination.status === PaymentDestinationStatus.paid) {
        return { kind: "paid" };
      }
      const stripeRedirect = destination.result;
      if (!stripeRedirect?.value) {
        throw new Error(
          "No checkout URL returned for an open payment destination",
        );
      }
      // Fire here, not right before the redirect, so the event isn't dropped when the page unloads.
      if (!checkoutStartedRef.current) {
        checkoutStartedRef.current = true;
        track(AD_CONVERSION_EVENTS.deprecatedCheckoutStarted, {
          event_context: {
            conversation_id: conversationId,
            user_progression_id: progressionId,
            product_option: productOption,
          },
        });
        const checkoutProperties = stripeRedirect.checkout_started_properties;
        if (checkoutProperties) {
          track(AD_CONVERSION_EVENTS.checkoutStarted, {
            ...checkoutProperties,
            $distinct_id: `checkout_started:${stripeRedirect.session_id}`,
          });
          // Needed as a workaround for how marketing has set up the Ours Privacy
          // Google Ads Integration.
          track(AD_CONVERSION_EVENTS.checkoutStartedAll, {
            ...checkoutProperties,
            $distinct_id: `checkout_started_all:${stripeRedirect.session_id}`,
          });
        }
      }
      return { kind: "open", checkoutUrl: stripeRedirect.value };
    }, [
      progressionId,
      conversationId,
      constructPaymentDestination,
      track,
      productOption,
    ]);

  const handleCheckoutError = useCallback(
    (error: unknown) => {
      if (isClinicRequestExpiredError(error)) {
        onClinicRequestExpired?.();
        return;
      }
      const resetProgression = getResetProgressionFromError(error);
      if (resetProgression) {
        onPaymentBlocked(resetProgression);
        return;
      }
      captureMessage("Failed to start checkout", { extra: { error } });
    },
    [onPaymentBlocked, onClinicRequestExpired],
  );

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    resolveDestination()
      .then((destination) => {
        if (cancelled || !destination) return;
        // Already paid — there's no checkout to open, so skip ahead without making the user wait
        // out the redirect delay.
        if (destination.kind === "paid") {
          advance();
          return;
        }
        // Open: keep the URL handy so the fallback link is instant, then redirect once the brief
        // screen has had its moment. The URL is already in hand, so the redirect itself is instant.
        setCheckoutUrl(destination.checkoutUrl);
        timer = setTimeout(() => {
          if (!cancelled) redirect(destination.checkoutUrl);
        }, AUTO_REDIRECT_DELAY_MS);
      })
      .catch((error) => {
        if (!cancelled) handleCheckoutError(error);
      });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [resolveDestination, advance, redirect, handleCheckoutError]);

  // Fallback link: go now rather than waiting out the delay. If the destination hasn't resolved
  // yet (or the first attempt failed), resolve it on demand.
  const continueNow = useCallback(async () => {
    if (checkoutUrl) {
      redirect(checkoutUrl);
      return;
    }
    try {
      const destination = await resolveDestination();
      if (!destination) return;
      if (destination.kind === "paid") {
        advance();
      } else {
        redirect(destination.checkoutUrl);
      }
    } catch (error) {
      handleCheckoutError(error);
    }
  }, [checkoutUrl, resolveDestination, advance, redirect, handleCheckoutError]);

  return (
    <YStack flex={1}>
      <Header paddingHorizontal="$250">
        <Heading size="$xl">
          You are being redirected to Stripe to pay...
        </Heading>
        <Paragraph size="$sm" color="$textSecondary">
          If you're not automatically redirected,{" "}
          <LinkText size="$sm" onPress={continueNow}>
            continue to payment
          </LinkText>
          .
        </Paragraph>
      </Header>
    </YStack>
  );
}
